import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import type { ApiError } from '@/api/http';
import { queryKeyRoots } from '@/api/queryKeys';
import {
  createQuestionSet,
  renameQuestionSet,
  type QuestionSet,
  type QuestionSetDetail,
} from '@/api/questionSets';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';

/**
 * `QuestionSetIn.name` is bounded at the column's own 200 characters, and the number is the
 * **document's** rather than a client's guess — the arrangement `questionFields.ts` established for
 * `QuestionIn.text` after BUG-058 shipped a client that let a writer type past a bound the server
 * enforced and then answered them with a sentence naming neither the field nor the limit.
 */
export const SET_NAME_MAX_LENGTH = 200;

const setFormSchema = z.object({
  name: z.string().trim().min(1).max(SET_NAME_MAX_LENGTH),
});

type SetForm = z.infer<typeof setFormSchema>;

interface Props {
  /**
   * The set being renamed, or **nothing at all** for a new one — which is what makes this one dialog
   * rather than two, exactly as `question?: Question` does for `QuestionDialog` and `vacancy?: Role`
   * for §6.8's. The field is identical on both paths.
   */
  set?: QuestionSet;
  onClose: () => void;
  /** Called with the created set, so the screen that opened this can go straight to composing it. */
  onCreated?: (set: QuestionSetDetail) => void;
}

/**
 * §6.9's **New set** and its **Rename** — one dialog, one field.
 *
 * ⚠️ **The form has exactly one field because `QuestionSetIn` has exactly one, and the omissions are
 * the decision.** No `author` (the session user is the author, and an author a client could assert
 * would be an author the write gate could not trust), no `is_active` (retiring is an **action**, ADR
 * 0005's shape as ADR 0010 set it) and no questions: composing a set is a separate act with its own
 * routes and its own per-row refusals, and it happens on the set's own screen.
 *
 * ⚠️ **Who may open which path is not symmetric, and neither half is asked here.** **New set** is open
 * to all three groups (ADR 0016 decision 5(c) — *"an `Interviewer` curates and does not assign"*), so
 * there is no predicate for it anywhere: one that could never be false where it is evaluated is
 * BUG-040's rule. **Rename** is the author's or an `Admin`'s, which is a fact about the **row**, so it
 * is asked where the row is (`SetMenu`, `mayWriteQuestionSet`) and §5 decides the rest — hidden, never
 * disabled. This dialog is never rendered for a viewer who may not write the set it holds.
 *
 * ⚠️ **Renaming a set changes no interview that was ever built from it.** The questions were frozen in
 * when each interview was arranged (ADR 0004, feedback protection #1) and a set's name is not part of
 * that record at all, so — unlike `QuestionDialog`'s edit path — there is no reassurance owed here:
 * there is nothing a reader could reasonably fear this does. The sentence that *is* owed lives on the
 * composition screen, where an edit really does change what later interviews ask.
 *
 * ⚠️ **A create invalidates `question-sets` and deliberately not `roles`.** A new set is attached to no
 * vacancy, so no `RoleOut.active_question_count` can have moved, and a second invalidation would be a
 * request that can only confirm what §6.8 already holds — the rule `QuestionDialog` had to learn in
 * Sprint 19, applied here before it could be got wrong.
 */
export function SetDialog({ set, onClose, onCreated }: Props) {
  const { t } = useTranslation(['questions', 'common', 'components']);
  const queryClient = useQueryClient();

  const renaming = set !== undefined;

  const form = useForm<SetForm>({
    // Mounted when it opens and unmounted when it closes, so the set as it is now *is* the default:
    // no effect resets the form, and a half-typed name cannot survive a refetch under it.
    resolver: zodResolver(setFormSchema),
    defaultValues: { name: set?.name ?? '' },
  });
  const { errors } = form.formState;

  const save = useMutation<QuestionSetDetail, ApiError, SetForm>({
    mutationFn: (submitted) =>
      set ? renameQuestionSet(set.id, { name: submitted.name }) : createQuestionSet(submitted),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: queryKeyRoots.questionSets });
      if (!renaming) onCreated?.(created);
      onClose();
    },
    /**
     * ⚠️ **A 404 on the rename path is *"this set is gone"*, and it is answered by re-reading rather
     * than under the field.** It covers a set another tenant owns, one that never existed and one
     * outside this reader's reach — indistinguishable by design (404 before 403) — so there is no
     * field to correct. A create has no 404 to answer: its path takes no id.
     */
    onError: (error) => {
      if (!error.isNotFound) return;
      void queryClient.invalidateQueries({ queryKey: queryKeyRoots.questionSets });
      onClose();
    },
  });

  const submit = form.handleSubmit(async (submitted) => {
    // Nothing was touched, so there is nothing to write: an empty `PATCH` answers 200 and moves
    // `updated_at` for no reason (`EditPersonDialog`'s finding, on the fourth screen it applies to).
    if (set && submitted.name === set.name) return onClose();

    try {
      await save.mutateAsync(submitted);
    } catch {
      // Held by the mutation and rendered below; rethrowing reaches only the console.
    }
  });

  /**
   * §8 inside a dialog: what failed, in words, never a status code and never the server's English
   * `detail` (§8, §11). Offline gets this screen's own sentence — status 0 means no answer arrived, so
   * on a create a confident retry is how one set ends up in the workspace twice, and the sentence names
   * the thing to reload before trying again.
   */
  const failure = (() => {
    if (!save.error || save.error.isNotFound) return null;
    if (save.error.isOffline) return t('questions:sets.dialog.offline');
    if (save.error.isThrottled) return t('components:state.throttled');
    return t(renaming ? 'questions:sets.rename.failed' : 'questions:sets.new.failed');
  })();

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        // Esc and the overlay cancel, and cancelling writes nothing. The amended `Dialog` (TASK-069)
        // owns the focus move and the way back to the opener; nothing here defeats it.
        if (!next) onClose();
      }}
      title={t(renaming ? 'questions:sets.rename.title' : 'questions:sets.new.title')}
      description={t(
        renaming ? 'questions:sets.rename.description' : 'questions:sets.new.description',
      )}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common:cancel')}
          </Button>
          <Button onClick={() => void submit()} isLoading={save.isPending}>
            {t(renaming ? 'questions:sets.rename.submit' : 'questions:sets.new.submit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <form className="flex flex-col gap-4" onSubmit={(event) => void submit(event)} noValidate>
          <Field
            label={t('questions:sets.form.name')}
            required
            error={errors.name ? t('questions:sets.form.errors.name') : undefined}
          >
            {(fieldProps) => (
              <Input {...fieldProps} {...form.register('name')} maxLength={SET_NAME_MAX_LENGTH} />
            )}
          </Field>
        </form>

        {failure && (
          <p role="alert" className="text-sm text-negative">
            {failure}
          </p>
        )}
      </div>
    </Dialog>
  );
}
